import { PropsWithChildren } from 'react';
import { Button, Tooltip, TooltipTrigger } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import Icon from '~/components/data-display/Icon';
import Card from '~/components/layout/Card';
import { HStack, VStack } from '~/components/layout/Stack';
import Text from '~/components/typographies/Text';
import CheckCircleIcon from '~/icons/icon-check-round-fill.svg?react';
import CheckIcon from '~/icons/icon-check.svg?react';
import CrossCircleIcon from '~/icons/icon-close-round-fill.svg?react';
import CrossIcon from '~/icons/icon-close.svg?react';
import InfoIcon from '~/icons/icon-info-round-stroke.svg?react';
import { Money } from '~/types/money';
import { formatMoney } from '~/utils/money';
import styles from './BookkeepingBalancingTooltip.module.scss';

interface Props {
  isBalanced: boolean;
  labels: string[];
}

interface BalancingTooltipRowProps {
  isBalanced: boolean;
  label: string;
  values: Money[];
}

export const BalancingTooltipRow: React.FC<PropsWithChildren<BalancingTooltipRowProps>> = ({
  isBalanced,
  label,
  values,
}) => {
  return (
    <tr>
      <td>
        <HStack gap={1} align="center" className={styles.balancedIndicator} justify="space-between">
          {label}
          {isBalanced ? (
            <Icon component={CheckCircleIcon} className={styles.check} size="sm" />
          ) : (
            <Icon component={CrossCircleIcon} className={styles.cross} size="sm" />
          )}
        </HStack>
      </td>
      {values.map((value, index) => (
        <td key={index}>{formatMoney(value)}</td>
      ))}
    </tr>
  );
};

const BookkeepingBalancingTooltip: React.FC<PropsWithChildren<Props>> = ({ isBalanced, labels, children }) => {
  const { t } = useTranslation('bookkeeping', { keyPrefix: 'balancing_tooltip' });

  return (
    <HStack gap={2} align="center">
      <HStack align="center" gap={1}>
        <Text variant="body2" weight="semibold" className={styles.differenceLabel}>
          {t('label')}
        </Text>
        <TooltipTrigger delay={0} closeDelay={0}>
          <Button className={styles.tooltipIcon}>
            <Icon component={InfoIcon} size="sm" />
          </Button>
          <Tooltip className={styles.tooltip}>
            <Card className={styles.tooltip} padding={2} shadow="medium">
              <VStack gap={2}>
                <HStack gap={1} align="center">
                  <Icon component={InfoIcon} size="sm" />
                  <Text variant="body2" weight="semibold">
                    {t('tooltip_title')}
                  </Text>
                </HStack>
                <table>
                  <thead>
                    <tr>
                      <th />
                      {labels.map((label) => (
                        <th key={label}>{label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>{children}</tbody>
                </table>
              </VStack>
            </Card>
          </Tooltip>
        </TooltipTrigger>
      </HStack>
      {isBalanced ? <CheckIcon className={styles.check} /> : <CrossIcon className={styles.cross} />}
    </HStack>
  );
};

export default BookkeepingBalancingTooltip;
